import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {SideNavComponent} from './layout/side-nav/side-nav.component';
import {ArticleModalComponent} from './modal/article-modal/article-modal.component';
import {SmartTableComponent} from './layout/smart-table/smart-table.component';
import {ModalFooterComponent} from './modal/modal-footer/modal-footer.component';
import {DeleteModalComponent} from './modal/delete-modal/delete-modal.component';
import {MainHeaderMenu} from './layout/main-header-menu/main-header-menu.component';
import {UserRoleSelectorComponent} from './input/user-role-selector/user-role-selector.component';
import {StreetTypeSelectorComponent} from './input/street-type-selector/street-type-selector.component';
import {CountrySelectorComponent} from './input/country-selector/country-selector.component';
import {ResizableModule} from 'angular-resizable-element';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {AngularDraggableModule} from 'angular2-draggable';
import {LocalizedDatePipe} from './pipes';
import {SafeHtmlPipe} from './pipes/safe-html';
import {HtmlEditorWrapperComponent} from './input/html-editor-wrapper/html-editor-wrapper.component';
import {CKEditorModule} from '@ckeditor/ckeditor5-angular';
import {SmartTabsComponent} from './layout/smart-tabs/smart-tabs.component';
import {CarouselWrapperComponent} from './layout/carousel-wrapper/carousel-wrapper.component';
import {NgxHmCarouselModule} from 'ngx-hm-carousel';
import {CarouselItemModalComponent} from './modal/carousel-item-modal/carousel-item-modal.component';
import {DropzoneModule} from 'ngx-dropzone-wrapper';
import {CustomDropZoneComponent} from './input/custom-drop-zone/custom-drop-zone.component';
import {UploadingSpinner} from './layout/uploading-spinner/uploading-spinner.component';
import {ReferenceModalComponent} from './modal/reference-modal/reference-modal.component';
import {TrapezeLabelComponent} from './layout/trapeze-label/trapeze-label.component';
import {NgxMasonryModule} from 'ngx-masonry';
import {GalleryGridComponent} from './layout/gallery-grid/gallery-grid.component';
import {ReferencePreviewComponent} from './layout/reference-preview/reference-preview.component';
import {ToastrModule} from 'ngx-toastr';
import {MobileHeaderComponent} from './layout/mobile-header/mobile-header.component';
import {PartnerModalComponent} from './modal/partner-modal/partner-modal.component';
import {AddressMapComponent} from './layout/address-map/address-map.component';
import {AgmCoreModule} from '@agm/core';
import {ArraySortPipe} from './pipes/sort-by.pipe';
import {FlatAreaTypeSelectorComponent} from './layout/flat-helpers/flat-area-type-selector/flat-area-type-selector.component';
import {FlatCoverageSelectorComponent} from './layout/flat-helpers/flat-coverage-selector/flat-coverage-selector.component';
import {FlatAreaEditorComponent} from './layout/flat-helpers/flat-area-editor/flat-area-editor.component';
import {FlatModalComponent} from './modal/flat-modal/flat-modal.component';
import {FlatLayoutSelectComponent} from './layout/flat-helpers/flat-layout-position-selector/flat-layout-select.component';
import {FlatFrontSelectComponent} from './layout/flat-helpers/flat-frontage-position-selector/flat-front-select.component';
import {ImageMapComponent} from './layout/image-map/image-map.component';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {FlatMapSectionComponent} from './layout/flat-map-section/flat-map-section.component';
import {BuildingSideSelectorComponent} from './layout/flat-helpers/building-side-selector/building-side-selector.component';
import {
  SuiAccordionModule,
  SuiCheckboxModule,
  SuiDatepickerModule,
  SuiDimmerModule,
  SuiDropdownModule,
  SuiMessageModule,
  SuiModalModule,
  SuiPopupModule,
  SuiRatingModule,
  SuiSelectModule,
  SuiSidebarModule,
  SuiTabsModule
} from 'ng2-semantic-ui';
import {NgxGalleryModule} from '@kolkov/ngx-gallery';
import {FlatPreviewItemComponent} from './layout/flat-helpers/flat-preview-item/flat-preview-item.component';
import {SoldOutComponent} from './layout/flat-helpers/sold-out/sold-out.component';
import {ProjectsMapComponent} from './layout/flat-helpers/projects-map/projects-map.component';
import {AdvantagesSectionComponent} from './layout/adventages-section/advantages-section.component';
import {GeneralInformationComponent} from './layout/general-information/general-information.component';
import {ContactSectionComponent} from './layout/contact-section/contact-section.component';
import {SectionHeadingComponent} from './layout/section-heading/section-heading.component';
import {MarketingItemModalComponent} from './modal/marketing-item-modal/marketing-item-modal.component';
import {MarketingItemComponent} from './layout/marketing-item/marketing-item.component';
import {RealEstateAgentModalComponent} from './modal/real-estate-agent-modal/real-estate-agent-modal.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCardModule} from '@angular/material/card';
import {MatChipsModule} from '@angular/material/chips';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatDialogModule} from '@angular/material/dialog';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {MatNativeDateModule, MatRippleModule} from '@angular/material/core';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatSliderModule} from '@angular/material/slider';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatTabsModule} from '@angular/material/tabs';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatRadioModule} from '@angular/material/radio';
import {MatSortModule} from '@angular/material/sort';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatStepperModule} from '@angular/material/stepper';
import {MatTableModule} from '@angular/material/table';
import { NgImageFullscreenViewModule } from 'ng-image-fullscreen-view';
import {GalleryModalComponent} from './modal/gallery-modal/gallery-modal.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    BrowserAnimationsModule,
    BrowserAnimationsModule,
    TranslateModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTableModule,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatStepperModule,
    ResizableModule,
    DragDropModule,
    AngularDraggableModule,
    CKEditorModule,
    NgxHmCarouselModule,
    DropzoneModule,
    NgxMasonryModule,
    ToastrModule,
    AgmCoreModule,
    LeafletModule,
    SuiSelectModule,
    SuiDropdownModule,
    SuiModalModule,
    SuiSelectModule,
    SuiSidebarModule,
    SuiTabsModule,
    SuiMessageModule,
    SuiCheckboxModule,
    SuiAccordionModule,
    SuiDatepickerModule,
    SuiDimmerModule,
    NgxGalleryModule,
    SuiPopupModule,
    SuiRatingModule,
    NgImageFullscreenViewModule
  ],
  declarations: [
    SideNavComponent,
    ArticleModalComponent,
    FlatModalComponent,
    ReferenceModalComponent,
    GalleryModalComponent,
    PartnerModalComponent,
    RealEstateAgentModalComponent,
    MarketingItemModalComponent,
    CarouselItemModalComponent,
    SmartTableComponent,
    ModalFooterComponent,
    DeleteModalComponent,
    MainHeaderMenu,
    SmartTabsComponent,
    UserRoleSelectorComponent,
    StreetTypeSelectorComponent,
    CountrySelectorComponent,
    BuildingSideSelectorComponent,
    LocalizedDatePipe,
    SafeHtmlPipe,
    HtmlEditorWrapperComponent,
    CarouselWrapperComponent,
    CustomDropZoneComponent,
    UploadingSpinner,
    TrapezeLabelComponent,
    GalleryGridComponent,
    ReferencePreviewComponent,
    MobileHeaderComponent,
    AddressMapComponent,
    ArraySortPipe,
    FlatAreaTypeSelectorComponent,
    FlatCoverageSelectorComponent,
    FlatAreaEditorComponent,
    FlatLayoutSelectComponent,
    FlatFrontSelectComponent,
    ImageMapComponent,
    FlatMapSectionComponent,
    FlatPreviewItemComponent,
    SoldOutComponent,
    ProjectsMapComponent,
    AdvantagesSectionComponent,
    GeneralInformationComponent,
    ContactSectionComponent,
    SectionHeadingComponent,
    MarketingItemComponent
  ],
  exports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTableModule,
    MatSortModule,
    MatStepperModule,
    BrowserAnimationsModule,
    SideNavComponent,
    ArticleModalComponent,
    FlatModalComponent,
    ReferenceModalComponent,
    GalleryModalComponent,
    PartnerModalComponent,
    RealEstateAgentModalComponent,
    MarketingItemModalComponent,
    CarouselItemModalComponent,
    SmartTableComponent,
    DeleteModalComponent,
    MainHeaderMenu,
    SmartTabsComponent,
    UserRoleSelectorComponent,
    StreetTypeSelectorComponent,
    CountrySelectorComponent,
    BuildingSideSelectorComponent,
    ResizableModule,
    DragDropModule,
    AngularDraggableModule,
    LocalizedDatePipe,
    SafeHtmlPipe,
    HtmlEditorWrapperComponent,
    CKEditorModule,
    CarouselWrapperComponent,
    CustomDropZoneComponent,
    UploadingSpinner,
    TrapezeLabelComponent,
    GalleryGridComponent,
    ReferencePreviewComponent,
    MobileHeaderComponent,
    AddressMapComponent,
    ArraySortPipe,
    ImageMapComponent,
    FlatMapSectionComponent,
    SuiSelectModule,
    SuiDropdownModule,
    SuiModalModule,
    SuiSelectModule,
    SuiSidebarModule,
    SuiTabsModule,
    SuiMessageModule,
    SuiCheckboxModule,
    SuiAccordionModule,
    SuiDatepickerModule,
    SuiDimmerModule,
    NgxGalleryModule,
    SuiPopupModule,
    SuiRatingModule,
    FlatPreviewItemComponent,
    SoldOutComponent,
    ProjectsMapComponent,
    AdvantagesSectionComponent,
    GeneralInformationComponent,
    ContactSectionComponent,
    SectionHeadingComponent,
    MarketingItemComponent,
    NgImageFullscreenViewModule
  ],
  entryComponents: [
    ArticleModalComponent,
    ReferenceModalComponent,
    GalleryModalComponent,
    DeleteModalComponent,
    CarouselItemModalComponent,
    PartnerModalComponent,
    RealEstateAgentModalComponent,
    FlatModalComponent,
    MarketingItemModalComponent
  ]
})
export class SharedModule {
}
